// Inline typeahead picker used by Add DerivedContext + Add Skill in the
// Roadmap scaffolds. Anchored absolutely to a trigger; opens with a
// text input + a filtered list of overlay candidates pulled from the
// shared roadmap-draft pool.
//
// Two outcomes per session:
//   - `onPick` — user selected an existing pool entry; the caller
//     should reuse its id so the new plan links to the same overlay
//     as whatever plan introduced it.
//   - `onCreate` — user typed a name that matched nothing and pressed
//     Enter; the caller mints a fresh id.
//
// The picker is intentionally light — no portal, no virtualization,
// no fuzzy match. The pool sizes we expect (≤ ~20 items) make a
// simple substring filter the right tool.

import { useEffect, useMemo, useRef, useState } from 'react';

export interface OverlayOption {
  id: string;
  display: string;
}

export function OverlayPicker({
  options,
  onPick,
  onCreate,
  onClose,
  placeholder,
  createLabel = 'Create',
  header,
  style,
}: {
  options: OverlayOption[];
  onPick: (option: OverlayOption) => void;
  onCreate: (display: string) => void;
  onClose: () => void;
  placeholder: string;
  /** Prefix for the "create new" affordance, e.g. "Create" → "Create 'Mobile'". */
  createLabel?: string;
  /** Optional caption above the input — used by Add Skill to surface
   *  which derivedContext the new skill will live under, since the picker
   *  renders at the bottom of the grid rather than next to the
   *  trigger. */
  header?: string;
  style?: React.CSSProperties;
}) {
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Dismiss when the user clicks outside the picker.
  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      if (!containerRef.current) return;
      if (containerRef.current.contains(e.target as Node)) return;
      onClose();
    }
    document.addEventListener('pointerdown', onPointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', onPointerDown, true);
  }, [onClose]);

  const trimmed = query.trim();
  const filtered = useMemo(() => {
    if (!trimmed) return options;
    const lo = trimmed.toLowerCase();
    return options.filter(o => o.display.toLowerCase().includes(lo));
  }, [options, trimmed]);

  // Show the "create" affordance whenever the user has typed something
  // that doesn't already exactly match an existing option (case-insensitive).
  const exactMatch = filtered.find(
    o => o.display.toLowerCase() === trimmed.toLowerCase()
  );
  const canCreate = trimmed.length > 0 && !exactMatch;

  // Combined row count for keyboard navigation: filtered options first,
  // then the synthetic "create" row when active.
  const rowCount = filtered.length + (canCreate ? 1 : 0);
  const clampedHighlight = rowCount === 0 ? -1 : Math.min(highlight, rowCount - 1);

  function commit(idx: number) {
    if (idx < 0 || idx >= rowCount) return;
    if (idx < filtered.length) {
      onPick(filtered[idx]!);
    } else if (canCreate) {
      onCreate(trimmed);
    }
  }

  return (
    <div
      ref={containerRef}
      className='snap-overlay-picker'
      style={style}
      onKeyDown={e => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          setHighlight(h => Math.min(h + 1, rowCount - 1));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setHighlight(h => Math.max(h - 1, 0));
        } else if (e.key === 'Enter') {
          e.preventDefault();
          if (clampedHighlight >= 0) commit(clampedHighlight);
          else if (canCreate) onCreate(trimmed);
        }
      }}
    >
      {header ? (
        <div className='snap-overlay-picker__header'>{header}</div>
      ) : null}
      <input
        ref={inputRef}
        className='snap-overlay-picker__input'
        value={query}
        onChange={e => {
          setQuery(e.target.value);
          setHighlight(0);
        }}
        placeholder={placeholder}
        aria-label={placeholder}
      />
      {rowCount > 0 ? (
        <ul className='snap-overlay-picker__list' role='listbox'>
          {filtered.map((o, i) => (
            <li
              key={o.id}
              role='option'
              aria-selected={i === clampedHighlight}
              className={`snap-overlay-picker__item${i === clampedHighlight ? ' is-highlighted' : ''}`}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => commit(i)}
            >
              <span className='snap-overlay-picker__item-label'>
                {o.display}
              </span>
              <span className='snap-overlay-picker__item-tag'>linked</span>
            </li>
          ))}
          {canCreate ? (
            <li
              role='option'
              aria-selected={clampedHighlight === filtered.length}
              className={`snap-overlay-picker__item snap-overlay-picker__item--create${clampedHighlight === filtered.length ? ' is-highlighted' : ''}`}
              onMouseEnter={() => setHighlight(filtered.length)}
              onClick={() => commit(filtered.length)}
            >
              <span className='snap-overlay-picker__item-label'>
                {createLabel} &lsquo;{trimmed}&rsquo;
              </span>
              <span className='snap-overlay-picker__item-tag snap-overlay-picker__item-tag--new'>
                new
              </span>
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
